import React from 'react';
import SEOHead from '../components/SEOHead';

const PrivacyPolicy = () => {
  const title = 'Privacy Policy | TradeRadar';
  const description =
    'Privacy Policy for TradeRadar, explaining how analytics, cookies, and limited contact data are used, and how users can manage their data and cookie preferences.';

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEOHead
        title={title}
        description={description}
        path="/privacy-policy"
        keywords="TradeRadar privacy policy, cookies, data protection"
      />

      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-3">
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant">
          Effective date: March 2026
        </p>
      </header>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          1. Overview
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          This Privacy Policy explains how TradeRadar collects, uses, and protects information when
          you visit the website or interact with its features. The platform is designed to minimise
          the amount of personal data collected and to use analytics and advertising technologies in
          line with applicable policies, including Google AdSense and analytics guidelines.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          2. Information Collected
        </h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2 list-disc list-inside">
          <li>
            <strong>Usage data:</strong> anonymised or pseudonymised information about how pages are
            accessed (for example, page views, time on page, device type, and approximate region).
          </li>
          <li>
            <strong>Cookies and similar technologies:</strong> small text files used to remember
            preferences, support analytics, and, where applicable, serve and measure advertising.
          </li>
          <li>
            <strong>Contact information:</strong> if you choose to send a message via the contact
            form, the name, email address, and message you provide are stored for the purpose of
            responding to your enquiry.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          3. How Information Is Used
        </h2>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2 list-disc list-inside">
          <li>To operate and improve the website and its content.</li>
          <li>To understand which pages are useful to visitors and where navigation can be improved.</li>
          <li>To respond to user enquiries sent via the contact form.</li>
          <li>
            To support the display and measurement of contextual advertising where enabled, in line
            with partner policies such as Google AdSense.
          </li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          4. Cookies and Third‑Party Services
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          The site may use cookies set directly by TradeRadar as well as by trusted third‑party
          providers, such as analytics platforms and advertising networks.
        </p>
        <ul className="text-sm text-slate-700 dark:text-on-surface-variant space-y-2 list-disc list-inside">
          <li>
            <strong>Analytics cookies</strong> help measure traffic, popular pages, and general
            usage patterns.
          </li>
          <li>
            <strong>Advertising cookies</strong> may be used by partners such as Google AdSense to
            serve and measure relevant, policy‑compliant ads.
          </li>
        </ul>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mt-2">
          You can control cookies through your browser settings and, where provided, through any
          on‑site consent or preferences tools. For how Google uses data when ads run on partner
          sites, see{' '}
          <a
            href="https://policies.google.com/technologies/partner-sites"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 dark:text-primary hover:underline"
          >
            How Google uses information from sites or apps that use our services
          </a>
          . You may also use Google&apos;s Ads Settings to manage ad personalization where available.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          5. Data Retention
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          Usage and analytics data are typically stored in aggregate or pseudonymised form for as
          long as needed to analyse trends and maintain the site. Contact form submissions may be
          retained for a reasonable period to allow follow‑up and maintain records of
          correspondence, after which they may be archived or deleted.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          6. Your Choices and Rights
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-2">
          Depending on your jurisdiction, you may have rights to request access to, correction of,
          or deletion of personal data relating to you. You can also typically opt out of certain
          types of cookies via your browser or device settings.
        </p>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          To make a request or ask a question about data usage, you can email{' '}
          <span className="font-mono">privacy@traderadarglobal.com</span>. For security reasons, the
          platform may need to take reasonable steps to verify your identity before responding.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-2">
          7. Changes to This Policy
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed">
          This Privacy Policy may be updated from time to time to reflect changes in the site,
          technology, or legal requirements. When updates are made, the &quot;Effective date&quot;
          at the top of the page will be revised. Continued use of the site after changes take
          effect will be taken as acceptance of the updated policy.
        </p>
      </section>
    </article>
  );
};

export default PrivacyPolicy;

